import dayjs from 'dayjs'
import sumBy from 'lodash/sumBy'
import { FlightParameters, Freessrs, Journey, Segment } from '../api'

// Helper functions for flight-related operations
export const formatDate = (dateString: string): string => {
  return dayjs(dateString).format('YYYY.MM.DD')
}

export const formatTime = (dateString: string): string => {
  return dayjs(dateString).format('HH:mm')
}

export const calculateTotalMinutes = (segments: Journey['segments']): number => {
  return sumBy(segments, seg => seg.flightTime + (seg.waitingTime || 0))
}

export const formatDuration = (totalMinutes: number): string => {
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  return `총 ${hours}시간 ${minutes}분`
}

export const calculateTotalFare = (journeys: Journey[]): number => {
  return sumBy(journeys, journey => journey.fares.totalFare ?? 0)
}

const JOURNEY_DELIMITER = '|'
const TRIP_TYPE_DELIMITER = '~'
const SEGMENT_DELIMITER = '>'
const FREE_BAGGAGE_INFO_PREFIX = '^'
const LAST_FIELD_DELIMITER = '-'

const convertToTripType = (journeyType: string): string => {
  switch (journeyType) {
    case 'OW':
      return 'ONE_WAY'
    case 'RT':
      return 'ROUND'
    case 'MD':
      return 'MULTI_CITY'
    default:
      return ''
  }
}

export const generateFlightInfoParams = (
  journeys: Journey[],
  flightSearchParams: FlightParameters,
): { itin: string; pps: string; promotionId: string } => {
  const { adultCount, childCount, infantCount } = flightSearchParams
  const itinerary = journeys
    .map(journey =>
      [generateKeyForJourney(journey), convertToTripType(journey.journeyType)].join(
        TRIP_TYPE_DELIMITER,
      ),
    )
    .join(JOURNEY_DELIMITER)

  const fareBasisKey = journeys
    .map(journey => generateKeyForFareBasis(journey))
    .join(JOURNEY_DELIMITER)

  const pps = generateKeyForPps(
    journeys[0].airline,
    fareBasisKey,
    adultCount,
    childCount,
    infantCount,
  )

  return {
    itin: itinerary,
    pps,
    promotionId: 'kyte',
  }
}

const generateKeyForPps = (
  validatingCarrier: string,
  fareBasisKey: string,
  adultCount?: number,
  childCount?: number,
  infantCount?: number,
): string => {
  const key: string[] = []

  if (adultCount) {
    key.push(`ADT${adultCount}${validatingCarrier}~${fareBasisKey}`)
  }

  if (childCount) {
    key.push(`CHD${childCount}${validatingCarrier}~${fareBasisKey}`)
  }

  if (infantCount) {
    key.push(`INF${infantCount}${validatingCarrier}~${fareBasisKey}`)
  }

  return key.join(',')
}

const generateKeyForFareBasis = (journey: Journey): string => {
  return journey.segments
    .map(segment => generateKeyForSegmentFareBasis(segment))
    .join(SEGMENT_DELIMITER)
}

const generateKeyForSegmentFareBasis = (segment: Segment): string => {
  return [convertCabinToInt(segment.cabinClass), segment.bookingClass, segment.fareBasisCode].join(
    LAST_FIELD_DELIMITER,
  )
}

// based on gds/gds_base.proto
//
// enum Cabin {
//     CABIN_UNKNOWN = 0;
//     CABIN_FIRST= 1; // F
//     CABIN_BUSINESS= 2; // C
//     CABIN_ECONOMY = 3; // Y (Stella, Sabre), M (Amadeus)
//     CABIN_ECONOMY_PREMIUM = 5; // P (Stella), S (Sabre), W (Amadeus)
// }
const convertCabinToInt = (cabinClass: string): number => {
  switch (cabinClass) {
    case 'F':
      return 1
    case 'C':
      return 2
    case 'Y':
    case 'M':
      return 3
    case 'P':
    case 'S':
    case 'W':
      return 4
    default:
      return 0
  }
}

const generateKeyForJourney = (journey: Journey): string => {
  const baggage = journey.freessrs?.find(freessr => freessr.ssrType === 'Baggage')
  const baggageKey = baggage ? FREE_BAGGAGE_INFO_PREFIX + generateKeyForBaggage(baggage) : ''
  return journey.segments
    .map(segment => generateKeyForSegment(segment) + baggageKey)
    .join(SEGMENT_DELIMITER)
}

const generateKeyForSegment = (segment: Segment): string => {
  return (
    `${segment.deptAirport}${segment.arrAirport}` +
    `${dayjs(segment.departureDate).format('YYYYMMDD')}${dayjs(segment.arrivalDate).format(
      'YYYYMMDD',
    )}` +
    `${segment.carrierCode}${Number(segment?.flightNumber) ?? 0}`
  )
}

const generateKeyForBaggage = (baggage: Freessrs): string => {
  const adtService = baggage.ssrService.find(service => service.paxType === 'ADT')
  if (!adtService) {
    return ''
  }

  switch (baggage.ssrUnit) {
    case 'KG':
      return `WEIGHT-KILOGRAM-${adtService.ssrValue}`
    case 'EA':
      return `PIECE-NONE-${adtService.ssrValue}`
    default:
      return ''
  }
}
